import EnvironmentManager from './environmentManager';
import { isWebviewMode } from './webviewMode';

export const USER_THEME_KEY = 'user-theme';
export const USER_THEME_COOKIE = 'user-theme';
export const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
export const DARK_MODE_FLAG_CACHE_KEY = 'dark-mode-flag-cache';
export const THEME_AUTO_DARK_POPUP_SEEN_KEY = 'theme-auto-dark-popup-seen';
// Efilli `user-theme` yoksa light yazar. Sistem dark ilk ziyarette Efilli için
// `user-theme=dark` yazılır; bu tercih değil, seed'dir (popup / system preference bozulmasın).
export const THEME_SYSTEM_DARK_SEED_KEY = 'theme-system-dark-seed';

export const THEME_AUTO_DARK_POPUP_CMS_KEYS = {
  visible: 'theme_system_auto_dark_info_visible',
  title: 'theme_system_auto_dark_info_title',
  textWeb: 'theme_system_auto_dark_info_text_web',
  textApp: 'theme_system_auto_dark_info_text_app',
  button1Text: 'theme_system_auto_dark_button_1_text',
  button2Text: 'theme_system_auto_dark_button_2_text',
  button1Deeplink: 'theme_system_auto_dark_button_1_deeplink'
};

export const THEME_AUTO_DARK_POPUP_FALLBACKS = {
  title: 'Karanlık Temaya Geçildi',
  textApp: 'Telefonunuzun sistem ayarında karanlık tema seçili olduğu için Bilyoner\'i bu şekilde açtık. Dilerseniz görünüm tercihinizi Ayarlar / Uygulama Teması menüsünden değiştirebilirsiniz.',
  textWeb: 'Bilgisayarınızın sistem ayarında karanlık tema seçili olduğu için Bilyoner\'i bu şekilde açtık. Dilerseniz görünüm tercihinizi Ayarlar / Uygulama Teması menüsünden değiştirebilirsiniz.',
  button1Text: 'Ayarlara Git',
  button2Text: 'Anladım',
  button1Deeplink: '/ayarlar'
};

export const USER_THEME_ATTR = 'data-theme';
export const USER_THEME_CHANGE_EVENT = 'user-theme-change';
export const USER_THEME_PREFERENCE_CHANGE_EVENT = 'user-theme-preference-change';
export const DEFAULT_THEME = 'light';
export const THEME_CHANGE_EVENT = 'bilyoner:themechange';
export const THEME_QUERY_PARAM = 'theme';

export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark'
};

// Kullanıcı tercihi. 'system' işletim sistemi/tarayıcı temasını takip eder;
// uygulanan tema (data-theme) her zaman THEMES değerlerinden biridir.
export const THEME_PREFERENCES = {
  ...THEMES,
  SYSTEM: 'system'
};

const isThemeValue = theme => theme === THEMES.DARK || theme === THEMES.LIGHT;

const isThemePreference = value => isThemeValue(value) || value === THEME_PREFERENCES.SYSTEM;

const isSystemDarkSeed = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return localStorage.getItem(THEME_SYSTEM_DARK_SEED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const persistSystemDarkSeed = () => {
  localStorage.setItem(USER_THEME_KEY, THEMES.DARK);
  localStorage.setItem(THEME_SYSTEM_DARK_SEED_KEY, '1');
};

const clearSystemDarkSeed = () => {
  localStorage.removeItem(THEME_SYSTEM_DARK_SEED_KEY);
};

export const SYSTEM_DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

// #root'un --background-secondary-background token'larıyla birebir aynı olmalı:
// harici CSS inmeden önceki ilk karede canvas bu renkle boyanır.
export const THEME_CANVAS_COLORS = {
  [THEMES.LIGHT]: '#f5f6f6',
  [THEMES.DARK]: '#2f3133'
};

const coerceQueryValue = value => {
  if (Array.isArray(value)) {
    return coerceQueryValue(value[0]);
  }

  return typeof value === 'string' ? value.trim().toLowerCase() : null;
};

const toQueryEntries = source => {
  let target = source;

  if (target === undefined || target === null) {
    if (typeof window === 'undefined') return null;
    target = window.location?.search ?? '';
  }

  if (typeof URLSearchParams !== 'undefined' && target instanceof URLSearchParams) {
    return target.entries();
  }

  if (typeof target === 'string') {
    return new URLSearchParams(target.startsWith('?') ? target.slice(1) : target).entries();
  }

  if (typeof target === 'object') {
    return Object.entries(target);
  }

  return null;
};

export const THEME_MIXPANEL_LABELS = {
  [THEMES.LIGHT]: 'Açık',
  [THEMES.DARK]: 'Koyu'
};

const THEME_MIXPANEL_SYSTEM_PREFIX = 'Sistem-';

export const THEME_CLICK_POINTS = {
  HAMBURGER: 'Hamburger',
  SETTINGS: 'Ayarlar',
  HEADER: 'Header'
};

const getThemeCmsValue = key => {
  const message = EnvironmentManager.messages?.find(item => item?.key === key);
  return String(message?.value ?? '').trim();
};

const isThemeFlagOn = key => getThemeCmsValue(key) === 'true';

const hasAuthoritativeCmsMessages = () =>
  EnvironmentManager.isLiveMessagesReady() && EnvironmentManager.messages?.length > 0;

let memoizedFlagMessages = null;
let memoizedDarkModeEnabled = null;

const readStoredFlagCache = () => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(DARK_MODE_FLAG_CACHE_KEY);
  } catch (e) {
    return null;
  }
};

const cacheDarkModeFlag = enabled => {
  if (typeof window === 'undefined') return;
  const next = enabled ? 'true' : 'false';
  try {
    if (localStorage.getItem(DARK_MODE_FLAG_CACHE_KEY) === next) return;
    localStorage.setItem(DARK_MODE_FLAG_CACHE_KEY, next);
  } catch (e) {
    // Eski WebView'lerde localStorage bootstrap'i durdurmasin
  }
};

const getAuthoritativeDarkModeEnabled = () => {
  if (!hasAuthoritativeCmsMessages()) {
    return null;
  }

  if (memoizedFlagMessages === EnvironmentManager.messages) {
    return memoizedDarkModeEnabled;
  }

  const enabled = isThemeFlagOn('dark_mode_enabled');
  memoizedFlagMessages = EnvironmentManager.messages;
  memoizedDarkModeEnabled = enabled;
  cacheDarkModeFlag(enabled);
  return enabled;
};

const isDarkModeEnabledByCms = () => getAuthoritativeDarkModeEnabled() === true;

export function isDarkModeEnabled () {
  const authoritative = getAuthoritativeDarkModeEnabled();

  if (authoritative !== null) {
    return authoritative;
  }

  // CMS yanıtı gelene kadar toggle'ın kaybolmaması için son bilinen flag kullanılır.
  return readStoredFlagCache() === 'true';
}

export function isSettingsThemeEnabled () {
  return isDarkModeEnabledByCms() && isThemeFlagOn('settings_theme_enabled');
}

export function isHamburgerThemeEnabled () {
  return isDarkModeEnabledByCms() && isThemeFlagOn('hamburger_theme_enabled');
}

export function getThemeLabels () {
  return {
    title: getThemeCmsValue('theme_title_name') || 'Uygulama Teması',
    dark: getThemeCmsValue('theme_dark_name') || 'Koyu',
    light: getThemeCmsValue('theme_light_name') || 'Açık',
    system: getThemeCmsValue('theme_system_name') || 'Sistem Ayarını Kullan'
  };
}

export function hasStoredThemePreference () {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    if (isSystemDarkSeed()) {
      return false;
    }

    return isThemePreference(localStorage.getItem(USER_THEME_KEY));
  } catch (e) {
    return true;
  }
}

export function isAutoDarkThemePopupSeen () {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    return localStorage.getItem(THEME_AUTO_DARK_POPUP_SEEN_KEY) === 'true';
  } catch (e) {
    return true;
  }
}

export function isAutoDarkThemePopupEnabled () {
  return isThemeFlagOn(THEME_AUTO_DARK_POPUP_CMS_KEYS.visible);
}

// Sistem teması dark + localStorage'da geçerli tema yok + CMS visible + popup daha önce görülmedi.
export function shouldShowAutoDarkThemePopup () {
  if (typeof window === 'undefined') {
    return false;
  }

  if (
    isWebviewMode()
    || !isDarkModeEnabled()
    || !isAutoDarkThemePopupEnabled()
    || isAutoDarkThemePopupSeen()
    || hasStoredThemePreference()
  ) {
    return false;
  }

  return getSystemTheme() === THEMES.DARK;
}

export function markAutoDarkThemePopupSeen () {
  if (typeof window === 'undefined') {
    return;
  }

  if (!hasStoredThemePreference()) {
    setUserTheme(THEME_PREFERENCES.SYSTEM);
  }

  try {
    localStorage.setItem(THEME_AUTO_DARK_POPUP_SEEN_KEY, 'true');
  } catch (e) {
    // Eski WebView'lerde localStorage bootstrap'i durdurmasin
  }
}

export function resolveAutoDarkThemePopupDeeplink (raw) {
  const fallback = THEME_AUTO_DARK_POPUP_FALLBACKS.button1Deeplink;
  const value = String(raw || '').trim();

  if (!value) {
    return fallback;
  }

  if (value.startsWith('/')) {
    return value;
  }

  try {
    const url = new URL(value);
    const host = url.hostname.replace(/^www\./, '');
    if (host === 'bilyoner.com' || host.endsWith('.bilyoner.com')) {
      return `${ url.pathname }${ url.search }${ url.hash }` || fallback;
    }
  } catch (e) {
    return fallback;
  }

  return value;
}

export function getAutoDarkThemePopupContent (isMobile) {
  const keys = THEME_AUTO_DARK_POPUP_CMS_KEYS;
  const fallbacks = THEME_AUTO_DARK_POPUP_FALLBACKS;

  return {
    title: getThemeCmsValue(keys.title) || fallbacks.title,
    text: getThemeCmsValue(isMobile ? keys.textApp : keys.textWeb)
      || (isMobile ? fallbacks.textApp : fallbacks.textWeb),
    button1Text: getThemeCmsValue(keys.button1Text) || fallbacks.button1Text,
    button2Text: getThemeCmsValue(keys.button2Text) || fallbacks.button2Text,
    button1Deeplink: resolveAutoDarkThemePopupDeeplink(
      getThemeCmsValue(keys.button1Deeplink) || fallbacks.button1Deeplink
    )
  };
}

export function getThemedCmsText (theme, lightKey, darkKey) {
  // Themed assets (fast-deposit brand/payment icons, etc.) live in main CMS.
  // getMessageText() switches to tjkMessages on /tjk routes, which leaves these
  // keys empty and breaks TJK insufficient-funds sheet logos/buttons.
  const lightValue = getThemeCmsValue(lightKey);

  if (normalizeTheme(theme) !== THEMES.DARK) {
    return lightValue;
  }

  const darkValue = darkKey ? getThemeCmsValue(darkKey) : '';
  return darkValue || lightValue;
}

export function normalizeTheme (theme) {
  return theme === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT;
}

const getSystemMediaQueryList = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }

  try {
    return window.matchMedia(SYSTEM_DARK_MEDIA_QUERY);
  } catch (e) {
    return null;
  }
};

export function getSystemTheme () {
  return getSystemMediaQueryList()?.matches ? THEMES.DARK : THEMES.LIGHT;
}

// Kayıt yoksa/geçersizse/okunamıyorsa 'system' (default).
export function getThemePreference () {
  if (typeof window === 'undefined') {
    return THEME_PREFERENCES.SYSTEM;
  }

  try {
    if (isSystemDarkSeed()) {
      return THEME_PREFERENCES.SYSTEM;
    }

    const stored = localStorage.getItem(USER_THEME_KEY);
    return isThemePreference(stored) ? stored : THEME_PREFERENCES.SYSTEM;
  } catch (e) {
    return THEME_PREFERENCES.SYSTEM;
  }
}

// Sistem tercihi yalnızca flag'in AÇIK olduğu biliniyorsa OS temasına çözümlenir (fail-safe, Z19).
const isSystemThemeAllowed = () => {
  const authoritative = getAuthoritativeDarkModeEnabled();
  return authoritative === true || (authoritative === null && readStoredFlagCache() === 'true');
};

export function resolveTheme (preference) {
  if (preference === THEME_PREFERENCES.SYSTEM) {
    // Flag bilinçli kapalıysa light. Aksi halde OS'u takip et — CMS gelene kadar
    // light varsayılanı ilk karede light flash üretir.
    if (isDarkModeForcedOff()) {
      return THEMES.LIGHT;
    }

    return getSystemTheme();
  }

  return normalizeTheme(preference);
}

// Dark mode CMS flag'i kapalıysa tema light'a zorlanır ve sistem teması dinlenmez/uygulanmaz.
const isDarkModeForcedOff = () => {
  const authoritative = getAuthoritativeDarkModeEnabled();
  return authoritative === false || (authoritative === null && readStoredFlagCache() === 'false');
};

export function getThemeFromQuery (source) {
  const entries = toQueryEntries(source);
  if (!entries) return null;

  for (const [key, value] of entries) {
    if (coerceQueryValue(key) !== THEME_QUERY_PARAM) continue;

    const normalized = coerceQueryValue(value);
    if (normalized === THEMES.DARK || normalized === THEMES.LIGHT) {
      return normalized;
    }

    return null;
  }

  return null;
}

export function getThemeFromCookie (cookieHeader) {
  if (typeof cookieHeader !== 'string' || !cookieHeader) return null;

  const entry = cookieHeader
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith(`${ USER_THEME_COOKIE }=`));

  if (!entry) return null;

  const value = coerceQueryValue(decodeURIComponent(entry.slice(USER_THEME_COOKIE.length + 1)));
  return isThemePreference(value) ? value : null;
}

// SSR yalnızca açık light|dark tercihini ve ?theme= query'sini html'e basar.
// Eski fail-safe'in yazdığı light cookie ilk ziyareti light boyamasın diye
// cookie=light / cookie=system yok sayılır; client init script + prefers-color-scheme karar verir.
export function getSsrDocumentTheme (query, cookieHeader) {
  const fromQuery = getThemeFromQuery(query);
  if (fromQuery) {
    return fromQuery;
  }

  const fromCookie = getThemeFromCookie(cookieHeader);
  return fromCookie === THEMES.DARK ? THEMES.DARK : null;
}

// Harici CSS inmeden once canvas'i temaya boyar; Rocket Loader gibi script erteleyen
// katmanlar inline <style>'a dokunmadigi icin ilk kare her kosulda dogru renkte olur.
export function getThemeCanvasCss () {
  const themed = Object.entries(THEME_CANVAS_COLORS)
    .map(([theme, color]) =>
      `html[${ USER_THEME_ATTR }="${ theme }"],html[${ USER_THEME_ATTR }="${ theme }"] body{background-color:${ color };color-scheme:${ theme };}`)
    .join('');

  const light = THEME_CANVAS_COLORS[THEMES.LIGHT];
  const dark = THEME_CANVAS_COLORS[THEMES.DARK];
  const unthemed = `html:not([${ USER_THEME_ATTR }]),html:not([${ USER_THEME_ATTR }]) body`;
  const fallback = `${ unthemed }{background-color:${ light };color-scheme:${ THEMES.LIGHT };}@media ${ SYSTEM_DARK_MEDIA_QUERY }{${ unthemed }{background-color:${ dark };color-scheme:${ THEMES.DARK };}}`;

  return `${ themed }${ fallback }`;
}

// Değer: Açık | Koyu | Sistem-Açık | Sistem-Koyu. Sistem tercihinde uygulanan temanın önüne
// "Sistem-" eklenir. Dark mode CMS flag'i kapalıyken tema zorla light olduğundan, kayıtlı tercih ne
// olursa olsun gerçekte uygulanan değer (Açık) raporlanır.
export function getThemeMixpanelValue (preference = getThemePreference()) {
  if (isDarkModeForcedOff()) {
    return THEME_MIXPANEL_LABELS[THEMES.LIGHT];
  }

  const label = THEME_MIXPANEL_LABELS[resolveTheme(preference)];
  return preference === THEME_PREFERENCES.SYSTEM ? `${ THEME_MIXPANEL_SYSTEM_PREFIX }${ label }` : label;
}

export function trackClickThemeButton (mixpanel, theme, clickPoint) {
  if (!mixpanel?.track) {
    return;
  }

  mixpanel.track('Click Theme Button', {
    Theme: getThemeMixpanelValue(theme),
    'Click Point': clickPoint
  });
}

const dispatchThemeChange = theme => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.dispatchEvent(new CustomEvent(USER_THEME_CHANGE_EVENT, { detail: theme }));
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: { theme } }));
  } catch (e) {
    // Eski WebView'lerde CustomEvent bootstrap'i durdurmasin
  }
};

const dispatchPreferenceChange = preference => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.dispatchEvent(new CustomEvent(USER_THEME_PREFERENCE_CHANGE_EVENT, { detail: { preference } }));
  } catch (e) {
    // Eski WebView'lerde CustomEvent bootstrap'i durdurmasin
  }
};

const persistThemeCookie = theme => {
  if (typeof document === 'undefined') return;

  try {
    const secure = window.location?.protocol === 'https:' ? '; Secure' : '';
    document.cookie
      = `${ USER_THEME_COOKIE }=${ theme }; path=/; max-age=${ THEME_COOKIE_MAX_AGE }; SameSite=Lax${ secure }`;
  } catch (e) {
    // Cookie yazilamazsa tema localStorage uzerinden calismaya devam eder
  }
};

export function getUserTheme () {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME;
  }

  if (isDarkModeForcedOff()) {
    return DEFAULT_THEME;
  }

  // Sistem modunda boyanmış attribute değil, anlık OS teması esas alınır.
  if (getThemePreference() === THEME_PREFERENCES.SYSTEM) {
    return resolveTheme(THEME_PREFERENCES.SYSTEM);
  }

  try {
    const painted = document.documentElement.getAttribute(USER_THEME_ATTR);
    if (painted === THEMES.DARK || painted === THEMES.LIGHT) {
      return painted;
    }

    return normalizeTheme(localStorage.getItem(USER_THEME_KEY));
  } catch (e) {
    return DEFAULT_THEME;
  }
}

export function isDarkTheme () {
  return getUserTheme() === THEMES.DARK;
}

export function applyTheme (theme) {
  if (typeof document === 'undefined') {
    return normalizeTheme(theme);
  }

  const normalized = normalizeTheme(theme);
  const root = document.documentElement;
  root.setAttribute(USER_THEME_ATTR, normalized);
  root.style.backgroundColor = THEME_CANVAS_COLORS[normalized];
  root.style.colorScheme = normalized;

  const preference = getThemePreference();
  persistThemeCookie(
    preference === THEMES.LIGHT || preference === THEMES.DARK
      ? preference
      : THEME_PREFERENCES.SYSTEM
  );

  return normalized;
}

// preference: 'light' | 'dark' | 'system'. Dönen değer uygulanan (çözümlenmiş) temadır.
export function setUserTheme (preference) {
  const normalized = isThemePreference(preference) ? preference : THEME_PREFERENCES.LIGHT;

  if (typeof window !== 'undefined') {
    try {
      if (
        normalized === THEME_PREFERENCES.SYSTEM
        && getSystemTheme() === THEMES.DARK
        && !isDarkModeForcedOff()
      ) {
        persistSystemDarkSeed();
      } else {
        clearSystemDarkSeed();
        localStorage.setItem(USER_THEME_KEY, normalized);
      }
    } catch (e) {
      console.error('[theme] failed to persist user theme:', e);
    }
  }

  const applied = syncThemeWithFeatureFlag(resolveTheme(normalized));
  dispatchPreferenceChange(normalized);

  return applied;
}

export function syncThemeWithFeatureFlag (preferredTheme) {
  const darkModeEnabled = getAuthoritativeDarkModeEnabled();
  // eslint-disable-next-line no-nested-ternary
  const nextTheme = darkModeEnabled === false
    ? DEFAULT_THEME
    : isThemeValue(preferredTheme) ? preferredTheme : getUserTheme();

  const current = typeof document !== 'undefined'
    ? document.documentElement.getAttribute(USER_THEME_ATTR)
    : null;
  const applied = applyTheme(nextTheme);

  if (applied !== current) {
    dispatchThemeChange(applied);
  }

  return applied;
}

export function subscribeUserTheme (callback) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleThemeChange = event => {
    callback(event?.detail ? normalizeTheme(event.detail) : getUserTheme());
  };

  const handleStorage = event => {
    if (event.key === USER_THEME_KEY || event.key === null) {
      callback(getUserTheme());
    }
  };

  window.addEventListener(USER_THEME_CHANGE_EVENT, handleThemeChange);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(USER_THEME_CHANGE_EVENT, handleThemeChange);
    window.removeEventListener('storage', handleStorage);
  };
}

// OS/tarayıcı teması değişince (yalnızca 'system' tercihinde ve dark mode flag'i açıkken)
// sayfa yenilenmeden temayı günceller.
export function subscribeSystemTheme () {
  const mediaQueryList = getSystemMediaQueryList();

  if (!mediaQueryList) {
    return () => {};
  }

  const handleChange = () => {
    if (getThemePreference() !== THEME_PREFERENCES.SYSTEM || !isSystemThemeAllowed()) {
      return;
    }

    try {
      if (getSystemTheme() === THEMES.DARK && !isDarkModeForcedOff()) {
        persistSystemDarkSeed();
      } else {
        clearSystemDarkSeed();
        localStorage.setItem(USER_THEME_KEY, THEME_PREFERENCES.SYSTEM);
      }
    } catch (e) {
      // Eski WebView'lerde localStorage bootstrap'i durdurmasin
    }

    syncThemeWithFeatureFlag(resolveTheme(THEME_PREFERENCES.SYSTEM));
  };

  // Safari < 14: addEventListener yok, addListener kullanılır.
  if (typeof mediaQueryList.addEventListener === 'function') {
    mediaQueryList.addEventListener('change', handleChange);
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }

  mediaQueryList.addListener?.(handleChange);
  return () => mediaQueryList.removeListener?.(handleChange);
}

export function initTheme (source) {
  const fromQuery = getThemeFromQuery(source);
  if (fromQuery) {
    return setUserTheme(fromQuery);
  }
  return applyTheme(getUserTheme());
}

export function getThemeInitScript () {
  const canvas = `{light:'${ THEME_CANVAS_COLORS[THEMES.LIGHT] }',dark:'${ THEME_CANVAS_COLORS[THEMES.DARK] }'}`;
  // html + body birlikte boyanır: UA stylesheet body'yi beyaz yaptigi icin sadece html yetmez.
  // Cookie da yazilir ki bir sonraki SSR istegi data-theme ile gelsin.
  const apply = `function(v,p){var d=document.documentElement;d.setAttribute('${ USER_THEME_ATTR }',v);d.style.backgroundColor=${ canvas }[v];d.style.colorScheme=v;if(document.body){document.body.style.backgroundColor=${ canvas }[v];}try{document.cookie='${ USER_THEME_COOKIE }='+(p||v)+'; path=/; max-age=${ THEME_COOKIE_MAX_AGE }; SameSite=Lax'+(location.protocol==='https:'?'; Secure':'');}catch(e){}}`;

  // Flag cache 'false' değilse ve kayıtlı light|dark yoksa OS temasını uygula (ilk ziyaret FOUC).
  // Sistem dark ise Efilli `user-theme=dark` beklediği için dark yazılır; seed ile tercih system kalır.
  // Cookie'ye system yazılır ki sonraki SSR light cookie ile data-theme=light basmasın.
  return `(function(){var a=${ apply };try{var t='light';var q='';var s=(location.search||'').replace(/^\\?/,'');var parts=s?s.split('&'):[];for(var i=0;i<parts.length;i++){try{var p=parts[i].split('=');var k=decodeURIComponent(p[0]||'').toLowerCase();if(k==='${ THEME_QUERY_PARAM }'){q=(decodeURIComponent((p[1]||'').replace(/\\+/g,' ')).toLowerCase()).replace(/^\\s+|\\s+$/g,'');break;}}catch(e){}}if(q==='dark'||q==='light'){t=q;try{localStorage.setItem('${ USER_THEME_KEY }',t);}catch(e){}a(t,t);}else{var flag=null;try{flag=localStorage.getItem('${ DARK_MODE_FLAG_CACHE_KEY }');}catch(e){}if(flag==='false'){t='light';a(t,'${ THEME_PREFERENCES.SYSTEM }');}else{var st=null;try{st=localStorage.getItem('${ USER_THEME_KEY }');}catch(e){}if(st==='dark'||st==='light'){t=st;a(t,t);}else{try{t=window.matchMedia&&window.matchMedia('${ SYSTEM_DARK_MEDIA_QUERY }').matches?'dark':'light';}catch(e){}if(t==='dark'){try{localStorage.setItem('${ USER_THEME_KEY }','dark');localStorage.setItem('${ THEME_SYSTEM_DARK_SEED_KEY }','1');}catch(e){}}a(t,'${ THEME_PREFERENCES.SYSTEM }');}}}}catch(e){a('light','${ THEME_PREFERENCES.SYSTEM }');}})();`;
}
